import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Plus, Pencil, Trash2, Loader2, Eye, Search, ChevronLeft, ChevronRight, Filter, Upload, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Available filter types for categories
const FILTER_TYPES = [
  { id: 'price', label: 'Price Range', labelRo: 'Interval preț' },
  { id: 'availability', label: 'Availability', labelRo: 'Disponibilitate' },
  { id: 'material', label: 'Material Type', labelRo: 'Tip material' },
  { id: 'bedsheet_type', label: 'Bedsheet Type', labelRo: 'Tip cearșaf' },
  { id: 'elastic', label: 'With/Without Elastic', labelRo: 'Cu/Fără elastic' },
  { id: 'bed_type', label: 'Bed Type', labelRo: 'Tip pat' },
  { id: 'color', label: 'Color', labelRo: 'Culoare' },
  { id: 'pieces', label: 'Number of Pieces', labelRo: 'Număr piese' },
  { id: 'bed_size', label: 'Bed Size', labelRo: 'Mărime pat' },
];

export const AdminCategories = () => {
  const { apiCall, API } = useAdmin();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [quickEditId, setQuickEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    is_active: 'no_change',
    sort_order: '',
    visible_filters: [],
  });
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    name: { ro: '', en: '' },
    slug: '',
    parent_id: '',
    description: { ro: '', en: '' },
    image: '',
    is_active: true,
    show_in_header: false,
    sort_order: 0,
    visible_filters: [],
  });

  const [quickEditData, setQuickEditData] = useState({
    name: { ro: '', en: '' },
    slug: '',
    parent_id: '',
    is_active: true,
    show_in_header: false,
    sort_order: 0,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await apiCall('/admin/categories');
      // Fetch product counts for each category
      const categoriesWithCounts = await Promise.all(
        (data || []).map(async (cat) => {
          try {
            const productsData = await apiCall(`/products?category=${cat.id}&limit=1`);
            return { ...cat, product_count: productsData.total || 0 };
          } catch {
            return { ...cat, product_count: 0 };
          }
        })
      );
      setCategories(categoriesWithCounts);
    } catch (err) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await apiCall(`/admin/categories/${editingCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Category updated');
      } else {
        await apiCall('/admin/categories', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Category created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleQuickEditSave = async (id) => {
    try {
      await apiCall(`/admin/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(quickEditData),
      });
      toast.success('Category updated');
      setQuickEditId(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await apiCall(`/admin/categories/${deleteConfirmId}`, { method: 'DELETE' });
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to delete category');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('files', file);
      
      const token = localStorage.getItem('rani_admin_token');
      const response = await fetch(`${API}/upload/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      if (data.uploaded && data.uploaded.length > 0) {
        setFormData(prev => ({ ...prev, image: data.uploaded[0].url }));
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || { ro: '', en: '' },
      slug: category.slug || '',
      parent_id: category.parent_id || '',
      description: category.description || { ro: '', en: '' },
      image: category.image || '',
      is_active: category.is_active ?? true,
      show_in_header: category.show_in_header ?? false,
      sort_order: category.sort_order || 0,
      visible_filters: category.visible_filters || [],
    });
    setIsDialogOpen(true);
  };

  const handleQuickEdit = (category) => {
    setQuickEditId(category.id);
    setQuickEditData({
      name: category.name || { ro: '', en: '' },
      slug: category.slug || '',
      parent_id: category.parent_id || '',
      is_active: category.is_active ?? true,
      show_in_header: category.show_in_header ?? false,
      sort_order: category.sort_order || 0,
    });
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: { ro: '', en: '' },
      slug: '',
      parent_id: '',
      description: { ro: '', en: '' },
      image: '',
      is_active: true,
      show_in_header: false,
      sort_order: 0,
      visible_filters: [],
    });
  };

  // Helper to get parent category name
  const getParentCategoryName = (parentId) => {
    const parent = categories.find(c => c.id === parentId);
    return parent ? (parent.name?.ro || parent.name?.en || 'Unknown') : '';
  };

  // Build hierarchical category tree
  const buildCategoryTree = (cats, parentId = null, level = 0) => {
    return cats
      .filter(c => (c.parent_id || null) === parentId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(c => ({
        ...c,
        level,
        children: buildCategoryTree(cats, c.id, level + 1)
      }));
  };

  // Flatten tree for display with indentation
  const flattenTree = (tree, result = []) => {
    for (const node of tree) {
      result.push(node);
      if (node.children?.length > 0) {
        flattenTree(node.children, result);
      }
    }
    return result;
  };

  const categoryTree = buildCategoryTree(categories);
  const flatCategories = flattenTree(categoryTree);

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const handleBulkAction = async () => {
    if (!bulkAction || selectedCategories.length === 0) return;
    
    if (bulkAction === 'bulk_edit') {
      setIsBulkEditOpen(true);
      return;
    }
    
    if (bulkAction === 'delete') {
      setBulkDeleteOpen(true);
      return;
    }
    
    try {
      if (bulkAction === 'activate') {
        await Promise.all(selectedCategories.map(id => 
          apiCall(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) })
        ));
        toast.success('Categories activated');
      } else if (bulkAction === 'deactivate') {
        await Promise.all(selectedCategories.map(id => 
          apiCall(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: false }) })
        ));
        toast.success('Categories deactivated');
      }
      setSelectedCategories([]);
      setBulkAction('');
      fetchCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      await Promise.all(selectedCategories.map(id => 
        apiCall(`/admin/categories/${id}`, { method: 'DELETE' })
      ));
      toast.success(`${selectedCategories.length} categories deleted`);
      setSelectedCategories([]);
      setBulkAction('');
      fetchCategories();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const handleBulkEditApply = async () => {
    if (selectedCategories.length === 0) return;
    
    try {
      const updateData = {};
      
      if (bulkEditData.is_active !== 'no_change') {
        updateData.is_active = bulkEditData.is_active === 'true';
      }
      if (bulkEditData.sort_order !== '') {
        updateData.sort_order = parseInt(bulkEditData.sort_order);
      }
      if (bulkEditData.visible_filters.length > 0) {
        updateData.visible_filters = bulkEditData.visible_filters;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.error('No changes to apply');
        return;
      }
      
      await Promise.all(selectedCategories.map(id => 
        apiCall(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(updateData) })
      ));
      
      toast.success(`${selectedCategories.length} categories updated`);
      setIsBulkEditOpen(false);
      setBulkEditData({
        is_active: 'no_change',
        sort_order: '',
        visible_filters: [],
      });
      setSelectedCategories([]);
      setBulkAction('');
      fetchCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleBulkFilter = (filterId) => {
    setBulkEditData(prev => {
      const current = prev.visible_filters || [];
      if (current.includes(filterId)) {
        return { ...prev, visible_filters: current.filter(f => f !== filterId) };
      } else {
        return { ...prev, visible_filters: [...current, filterId] };
      }
    });
  };

  const toggleFilterVisibility = (filterId) => {
    setFormData(prev => {
      const current = prev.visible_filters || [];
      if (current.includes(filterId)) {
        return { ...prev, visible_filters: current.filter(f => f !== filterId) };
      } else {
        return { ...prev, visible_filters: [...current, filterId] };
      }
    });
  };

  const viewCategoryProducts = (categoryId) => {
    navigate(`/rani-admin/products?category=${categoryId}`);
  };

  // Filter and paginate categories
  // Filter categories by search term, then use hierarchical ordering
  const searchedCategories = categories.filter(cat => 
    (cat.name?.ro || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.name?.en || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.slug || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // When searching, show flat list; otherwise show hierarchical
  const filteredCategories = searchTerm 
    ? searchedCategories 
    : flatCategories.filter(cat => searchedCategories.some(sc => sc.id === cat.id) || !searchTerm);

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Categories</h2>
          <p className="text-muted-foreground">Manage product categories and their filters</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="add-category-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name (RO) *</Label>
                  <Input
                    value={formData.name.ro}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ro: e.target.value } })}
                    required
                  />
                </div>
                <div>
                  <Label>Name (EN)</Label>
                  <Input
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Slug *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Parent Category</Label>
                  <Select
                    value={formData.parent_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, parent_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No Parent (Top Level) —</SelectItem>
                      {categories
                        .filter(c => c.id !== editingCategory?.id) // Can't be parent of itself
                        .map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {'—'.repeat(c.level || 0)} {c.name?.ro || c.name?.en}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Description (RO)</Label>
                  <Input
                    value={formData.description?.ro || ''}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ro: e.target.value } })}
                  />
                </div>
                <div>
                  <Label>Description (EN)</Label>
                  <Input
                    value={formData.description?.en || ''}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <Label>Category Image</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full text-sm border rounded-lg p-2"
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
                    <p className="text-xs text-muted-foreground mt-1">Or enter image URL below:</p>
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="https://..."
                      className="mt-2"
                    />
                  </div>
                  {formData.image && (
                    <div className="relative">
                      <img 
                        src={formData.image.startsWith('/api') ? `${API}${formData.image}` : formData.image} 
                        alt="Preview" 
                        className="w-20 h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image: '' })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Filter Visibility Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="flex items-center gap-2 mb-3">
                  <Filter className="h-4 w-4" />
                  Visible Filters for this Category
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select which filters customers can use when browsing this category
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {FILTER_TYPES.map((filter) => (
                    <label key={filter.id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100">
                      <Checkbox
                        checked={(formData.visible_filters || []).includes(filter.id)}
                        onCheckedChange={() => toggleFilterVisibility(filter.id)}
                      />
                      <span className="text-sm">{filter.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span>Active</span>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.show_in_header}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_header: checked })}
                />
                <span>Show in Header Menu</span>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingCategory ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Bulk Actions and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={bulkAction} onValueChange={setBulkAction}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Bulk actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bulk_edit">Bulk Edit</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="activate">Activate</SelectItem>
              <SelectItem value="deactivate">Deactivate</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleBulkAction} disabled={!bulkAction || selectedCategories.length === 0}>
            Apply
          </Button>
          <span className="text-sm text-muted-foreground ml-2">
            {filteredCategories.length} items
          </span>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : paginatedCategories.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No categories found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="py-3 px-4 w-10">
                      <Checkbox
                        checked={selectedCategories.length === paginatedCategories.length && paginatedCategories.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCategories(paginatedCategories.map(c => c.id));
                          } else {
                            setSelectedCategories([]);
                          }
                        }}
                      />
                    </th>
                    <th className="text-left py-3 px-4">Image</th>
                    <th className="text-left py-3 px-4">Name / Parent</th>
                    <th className="text-left py-3 px-4">Description</th>
                    <th className="text-left py-3 px-4">Slug</th>
                    <th className="text-center py-3 px-4">Products</th>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCategories.map((cat) => (
                    <React.Fragment key={cat.id}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Checkbox
                            checked={selectedCategories.includes(cat.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCategories([...selectedCategories, cat.id]);
                              } else {
                                setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                              }
                            }}
                          />
                        </td>
                        <td className="py-3 px-4">
                          {cat.image ? (
                            <img src={cat.image} alt="" className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400">-</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => viewCategoryProducts(cat.id)}
                            className="font-medium text-primary hover:underline text-left"
                          >
                            <span style={{ paddingLeft: `${(cat.level || 0) * 20}px` }}>
                              {cat.level > 0 && <span className="text-gray-400 mr-1">{'└─'.repeat(1)}</span>}
                              {cat.name?.ro || cat.name?.en}
                            </span>
                          </button>
                          {cat.parent_id && (
                            <div className="text-xs text-blue-500 mt-0.5" style={{ paddingLeft: `${(cat.level || 0) * 20}px` }}>
                              ↳ Parent: {getParentCategoryName(cat.parent_id)}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1" style={{ paddingLeft: `${(cat.level || 0) * 20}px` }}>
                            ID: {cat.id?.slice(0, 8)}
                          </div>
                          <div className="flex gap-2 mt-1 text-xs" style={{ paddingLeft: `${(cat.level || 0) * 20}px` }}>
                            <button type="button" onClick={() => handleEdit(cat)} className="text-primary hover:underline">Edit</button>
                            <span className="text-gray-300">|</span>
                            <button type="button" onClick={() => handleQuickEdit(cat)} className="text-primary hover:underline">Quick Edit</button>
                            <span className="text-gray-300">|</span>
                            <button type="button" onClick={() => handleDeleteClick(cat.id)} className="text-red-500 hover:underline">Delete</button>
                            <span className="text-gray-300">|</span>
                            <button type="button" onClick={() => viewCategoryProducts(cat.id)} className="text-primary hover:underline">View</button>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground max-w-xs truncate">
                          {cat.description?.ro || cat.description?.en || '-'}
                        </td>
                        <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{cat.slug}</td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => viewCategoryProducts(cat.id)}
                            className="text-primary hover:underline font-medium"
                          >
                            {cat.product_count || 0}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-xs text-muted-foreground">
                          <div title="Created">
                            📅 {cat.created_at ? new Date(cat.created_at).toLocaleDateString() : '-'}
                          </div>
                          {cat.updated_at && cat.updated_at !== cat.created_at && (
                            <div title="Modified" className="text-blue-500">
                              ✏️ {new Date(cat.updated_at).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {cat.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                      
                      {/* Quick Edit Row */}
                      {quickEditId === cat.id && (
                        <tr className="bg-blue-50 border-b">
                          <td colSpan={8} className="p-4">
                            <div className="flex flex-wrap gap-4 items-end">
                              <div>
                                <Label className="text-xs">Name (RO)</Label>
                                <Input
                                  value={quickEditData.name.ro}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, name: { ...quickEditData.name, ro: e.target.value } })}
                                  className="w-40 h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Name (EN)</Label>
                                <Input
                                  value={quickEditData.name.en}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, name: { ...quickEditData.name, en: e.target.value } })}
                                  className="w-40 h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Slug</Label>
                                <Input
                                  value={quickEditData.slug}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, slug: e.target.value })}
                                  className="w-32 h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Order</Label>
                                <Input
                                  type="number"
                                  value={quickEditData.sort_order}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, sort_order: parseInt(e.target.value) || 0 })}
                                  className="w-20 h-8"
                                />
                              </div>
                              <label className="flex items-center gap-2">
                                <Checkbox
                                  checked={quickEditData.is_active}
                                  onCheckedChange={(checked) => setQuickEditData({ ...quickEditData, is_active: checked })}
                                />
                                <span className="text-xs">Active</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <Checkbox
                                  checked={quickEditData.show_in_header}
                                  onCheckedChange={(checked) => setQuickEditData({ ...quickEditData, show_in_header: checked })}
                                />
                                <span className="text-xs">In Header</span>
                              </label>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleQuickEditSave(cat.id)} className="bg-primary h-8">
                                  Update
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setQuickEditId(null)} className="h-8">
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              «
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Edit Dialog for Categories */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Edit - {selectedCategories.length} Categories Selected</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Left Column - Selected Categories */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Selected Categories</Label>
              <div className="border rounded-lg max-h-60 overflow-y-auto p-2 bg-gray-50">
                {filteredCategories
                  .filter(cat => selectedCategories.includes(cat.id))
                  .map(cat => (
                    <div key={cat.id} className="flex items-center gap-2 py-1 text-sm">
                      <button 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setSelectedCategories(selectedCategories.filter(id => id !== cat.id))}
                      >
                        ✕
                      </button>
                      <span className="truncate">{cat.name?.ro || cat.name?.en}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Right Column - Edit Fields */}
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={bulkEditData.is_active}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, is_active: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Sort Order</Label>
                <Select 
                  value={bulkEditData.sort_order === '' ? 'no_change' : 'custom'}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, sort_order: v === 'no_change' ? '' : bulkEditData.sort_order })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="custom">Set sort order</SelectItem>
                  </SelectContent>
                </Select>
                {bulkEditData.sort_order !== '' && (
                  <Input
                    type="number"
                    placeholder="Enter sort order"
                    value={bulkEditData.sort_order}
                    onChange={(e) => setBulkEditData({ ...bulkEditData, sort_order: e.target.value })}
                    className="mt-2"
                  />
                )}
              </div>

              <div>
                <Label className="mb-2 block">Visible Filters</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 bg-gray-50 space-y-1">
                  {FILTER_TYPES.map((filter) => (
                    <label key={filter.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <Checkbox
                        checked={(bulkEditData.visible_filters || []).includes(filter.id)}
                        onCheckedChange={() => toggleBulkFilter(filter.id)}
                      />
                      <span className="text-sm">{filter.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkEditApply} className="bg-primary">
              Update {selectedCategories.length} Categories
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Category
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this category? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedCategories.length} Categories
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete these categories? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDeleteConfirm}>
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
